/**
 * GeneralMapView Component (Web)
 *
 * A React web map view using Google Maps API with clustering, markers, and custom callouts.
 * Includes Floating Action Buttons (FABs) for map reset and navigation.
 *
 * Props:
 * - `data`: Array of marker data.
 * - `initialCenter`: Initial center coordinates for the map.
 * - `onNavToListView`: Callback for navigation to list view.
 * - `onNavToDetailView`: Callback for navigation to detail view.
 * - `markerIcons`: Custom icons for active/inactive markers.
 * - `calloutProps`: Props for customizing callouts.
 *
 * Dependencies:
 * - @react-google-maps/api
 * - FontAwesome icons
 */
import React, { useRef, useState, useEffect } from "react";
import { GoogleMap, Marker, MarkerClusterer, InfoWindow } from "@react-google-maps/api";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faList, faSatellite, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { faLocationDotSlash } from "@fortawesome/pro-solid-svg-icons";
import CustomFab from "../../../components/CustomFAB/CustomFAB";
import CustomCallout from "../../../components/CustomCallout/CustomCallout.web";
import GoogleMapsLoader from "../../../components/GoogleMapsLoader/GoogleMapsLoader.web";
import ClusterMarker from "../../../assets/images/cluster-marker.png";
import { COLORS, STRINGS } from "../../../constants";
import { sanitizeCoordinate } from "../../../utils/formatting";
import "./MapStyles.css";
import { useBottomTabBarHeight } from "@react-navigation/bottom-tabs";
import AppSettings from "../../../storage/appSettings";
import ParkingStore from "../../../storage/parkingStore";

const PermissionModal = ({ onDismiss }) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      style={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-permission-modal-text"
      onKeyDown={(e) => {
        if (e.key === "Escape") onDismiss();
      }}
      tabIndex={-1}
    >
      <div style={styles.modalContent}>
        <p id="location-permission-modal-text" style={styles.modalText}>
          {STRINGS.locationPermissionMessage}
        </p>
        <button
          ref={buttonRef}
          style={styles.modalButton}
          onClick={onDismiss}
        >
          Okay
        </button>
      </div>
    </div>
  );
};

const GeneralMapView = ({ data, initialCenter, onNavToListView, onNavToDetailView, markerIcons, calloutProps }) => {
  const toggleSatelliteView = AppSettings((state) => state.toggleSatelliteView);
  const satelliteView = AppSettings((state) => state.satelliteView);
  const showUserLocation = AppSettings((state) => state.showUserLocation);
  const toggleShowUserLocation = AppSettings((state) => state.toggleShowUserLocation);
  const showMeter = ParkingStore((state) => state.showMeter);
  const showStructure = ParkingStore((state) => state.showStructure);
  const parkingData = ParkingStore((state) => state.parking);
  const bottomTabBarHeight = useBottomTabBarHeight();
  const mapRef = useRef(null);

  const [activeMarker, setActiveMarker] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationGranted, setLocationGranted] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);

  useEffect(() => {
    let permissionStatus = null;
    let cancelled = false;

    const fallbackCheck = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          () => { if (!cancelled) setLocationGranted(true); },
          () => { if (!cancelled) setLocationGranted(false); }
        );
      } else {
        setLocationGranted(false);
      }
    };

    const checkPermission = async () => {
      try {
        permissionStatus = await navigator.permissions.query({ name: "geolocation" });
        if (!cancelled) setLocationGranted(permissionStatus.state === "granted");

        permissionStatus.onchange = () => {
          if (!cancelled) setLocationGranted(permissionStatus.state === "granted");
        };
      } catch {
        // Permissions API unavailable for geolocation (e.g., Safari) — probe directly
        fallbackCheck();
      }
    };

    if (navigator.permissions) {
      checkPermission();
    } else {
      fallbackCheck();
    }

    return () => {
      cancelled = true;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    if (locationGranted) {
      setPermissionModalVisible(false);
    }
  }, [locationGranted]);

  useEffect(() => {
    let cancelled = false;
    if (locationGranted && showUserLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!cancelled) {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          }
        },
        () => {}
      );
    } else if (!showUserLocation || !locationGranted) {
      setUserLocation(null);
    }
    return () => { cancelled = true; };
  }, [showUserLocation, locationGranted]);

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    fullscreenControl: false,
    controlSize: 32,
    mapTypeControl: false,
    clickableIcons: false,
    streetViewControl: false,
  };

  const clusterStyles = [
    {
      url: ClusterMarker,
      height: 40,
      width: 40,
      textColor: COLORS.textLight,
      textSize: 12,
    },
  ];

  useEffect(() => {
    const selectedParkingType = parkingData.find((parking) => parking.id === activeMarker)?.type;
    if (activeMarker && !showMeter && !selectedParkingType) {
      setActiveMarker(null);
    } else if (activeMarker && !showStructure && !selectedParkingType) {
      setActiveMarker(null);
    }
  }, [showMeter, showStructure]);

  /**
   * Resets the map view to its initial center and zoom level.
   */
  const handleResetMap = () => {
    if (mapRef.current) {
      mapRef.current.setCenter(initialCenter);
      mapRef.current.setZoom(12);
    }
    setActiveMarker(null);
  };

  /**
   * Handles marker click events.
   * Sets the active marker or deselects it if already selected.
   *
   * @param {string} markerId - The ID of the clicked marker.
   */
  const handleMarkerClick = (markerId) => {
    if (markerId !== activeMarker) {
      setActiveMarker(markerId);
    } else {
      setActiveMarker(null);
    }
  };
  /**
   * Generate a unique ID for locations without an ID.
   * @param {object} location - The location object.
   * @param {number} index - The index of the location in the array.
   * @returns {string} - A unique ID for the location.
   */
  const generateMarkerId = (location, index) => {
    return location.id || `${location.name}-${location.latitude}-${index}`;
  };

  const handleLocationFabPress = () => {
    if (locationGranted) {
      toggleShowUserLocation();
    } else {
      setPermissionModalVisible(true);
    }
  };

  return (
    <GoogleMapsLoader>
    <div
      style={{
        ...styles.container,
        height: `calc(100vh - ${bottomTabBarHeight}px)`,
      }}
    >
      <div style={styles.mapWrapper}>
        <GoogleMap
          mapContainerStyle={styles.mapContainer}
          defaultCenter={initialCenter}
          zoom={12}
          onLoad={(map) => {
            mapRef.current = map;
            handleResetMap();
          }}
          options={{
            ...mapOptions,
            mapTypeId: satelliteView ? "satellite" : "roadmap",
          }}
          mapContainerClassName="custom-map-controls"
        >
          <MarkerClusterer options={{ styles: clusterStyles }}>
            {(clusterer) =>
              data.map((location, index) => {
                // Get the latitude and longitude from the location object.  Bathroom locations have a geometry object with x and y properties, while other locations have latitude and longitude properties.
                const latitude = location.geometry
                  ? sanitizeCoordinate(location.geometry.y.N)
                  : sanitizeCoordinate(location.latitude);
                const longitude = location.geometry
                  ? sanitizeCoordinate(location.geometry.x.N)
                  : sanitizeCoordinate(location.longitude);
                const markerId = generateMarkerId(location, index);
                const icons = typeof markerIcons === "function" ? markerIcons(location) : markerIcons;
                return (
                  <Marker
                    key={markerId}
                    data-testid={`map-marker-${markerId}`}
                    title={calloutProps.title(location)}
                    position={{
                      lat: latitude,
                      lng: longitude,
                    }}
                    clusterer={clusterer}
                    icon={activeMarker === markerId ? icons.enabled : icons.disabled}
                    onClick={() => handleMarkerClick(markerId)}
                  >
                    {activeMarker === markerId && (
                      <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                        <CustomCallout
                          title={calloutProps.title(location)}
                          subtitle={calloutProps.subtitle(location)}
                          {...(calloutProps?.chipProps ? { chipProps: calloutProps.chipProps(location) } : {})}
                          onPress={() => {
                            setActiveMarker(null);
                            onNavToDetailView(location);
                          }}
                        />
                      </InfoWindow>
                    )}
                  </Marker>
                );
              })
            }
          </MarkerClusterer>
          {locationGranted && showUserLocation && userLocation && (
            <Marker
              position={userLocation}
              clickable={false}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
                scale: 8,
                fillColor: "#4285F4",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 2,
              }}
              zIndex={999}
            />
          )}
        </GoogleMap>
        {/* FAB Controls */}
        <div style={styles.fabSatelliteContainer}>
          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={toggleSatelliteView}
              icon={faSatellite}
              size="2x"
              color={COLORS.textLight}
              backgroundColor={satelliteView ? COLORS.secondary : COLORS.textDark}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="Satellite"
            />
          </div>
          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={handleLocationFabPress}
              icon={locationGranted ? faLocationDot : faLocationDotSlash}
              size="2x"
              color={COLORS.textLight}
              backgroundColor={locationGranted && showUserLocation ? COLORS.secondary : COLORS.textDark}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="Toggle user location"
            />
          </div>
        </div>
        <div style={{ ...styles.fabContainer }}>
          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={handleResetMap}
              icon={faRotateRight}
              size="lg"
              color={COLORS.textLight}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="Reset"
            />
          </div>

          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={onNavToListView}
              icon={faList}
              size="lg"
              color={COLORS.textLight}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="List View"
            />
          </div>
        </div>
      </div>
    </div>
    {permissionModalVisible && (
      <PermissionModal onDismiss={() => setPermissionModalVisible(false)} />
    )}
    </GoogleMapsLoader>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
  },
  mapWrapper: {
    flex: 1,
    width: "100%",
  },
  mapContainer: {
    height: "100%",
    width: "100%",
  },
  fabContainer: {
    position: "absolute",
    bottom: "16px",
    right: "0",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    zIndex: 10,
  },
  fabWrapper: {
    backgroundColor: "transparent",
    borderRadius: 50,
    width: "56px",
    height: "56px",
  },
  fabSatelliteContainer: {
    position: "absolute",
    bottom: "16px",
    left: 32,
    zIndex: 10,
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    marginLeft: 32,
    marginRight: 32,
    textAlign: "center",
    maxWidth: 400,
  },
  modalText: {
    fontSize: 16,
    color: "#333",
    margin: 0,
    marginBottom: 20,
  },
  modalButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.textLight,
    border: "none",
    borderRadius: 20,
    padding: "12px 24px",
    fontSize: 18,
    fontWeight: "bold",
    cursor: "pointer",
  },
};

export default GeneralMapView;
